<template>
    <modal
        :active="active"
        @closed="$emit('closed', true)"
        :title="title"
        :large="true"
    >
        <div class="tabs">
            <ul>
                <li
                    v-for="tab in tabs"
                    :key="tab.key"
                    :class="[
                        {'is-active': activeTab === tab.key},
                        {'is-disabled': tab.disabled},
                    ]"
                    @click.prevent="setActiveTab(tab)"
                >
                    <a>{{ tab.title }}</a>
                </li>
            </ul>
        </div>

        <template v-if="activeTab === 'details'">
            <div class="columns is-tablet mt-1">
                <form-field
                    class="column"
                    label="Billede"
                    help-text="Dimensioner: 780x475"
                    :error="error.uploadedFile"
                >
                    <div v-if="image" class="item-category-image">
                        <img :src="image.url" :alt="image.name" />
                        <div class="item-category-image--name">{{ image.name }}</div>
                        <div class="item-category-image--actions">
                            <div v-if="itemCategory?.image" class="item-category-image--actions--download" @click="downloadImage">
                                Download
                                <span class="icon">
                                    <i class="fas fa-download"></i>
                                </span>
                            </div>
                            <div class="item-category-image--actions--remove" @click="removeImage">
                                {{ itemCategory?.image ? 'Slet image' : 'Fjern' }}
                                <span class="icon">
                                    <i class="fas fa-times"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                    <InputDropzone
                        v-else
                        clickable
                        accepted-files="image/*"
                        :auto-process-queue="false"
                        :url="'/api/item-categories/upload-image'"
                        @success="fileAdded"
                    />
                </form-field>
            </div>

            <div class="tabs">
                <ul>
                    <li v-for="locale in listLocales" :key="locale.id" :class="{'is-active': locale_id === locale.id}" @click.prevent="locale_id = locale.id">
                        <a>
                            {{ locale.name }}
                            <span class="icon" v-if="!form.localizations?.find((localization) => localization.locale_id === locale.id && localization.key === 'name')">
                                <i class="fas fa-exclamation-triangle"></i>
                            </span>
                        </a>
                    </li>
                </ul>
            </div>
            <div class="columns is-multiline mb-1">
                <form-field
                    class="column is-12"
                    :error="error.localizations"
                    label="Kategori"
                >
                    <input-text
                        v-model="name"
                        :error="error.localizations"
                        @input="localizationChanged('name')"
                    />
                </form-field>
                <form-field
                    class="column is-full-mobile is-half-desktop"
                    :error="error.localizations"
                    label="Underoverskrift"
                >
                    <InputTextarea
                        v-model="subtitle"
                        :error="error.localizations"
                        rows="3"
                        @input="localizationChanged('subtitle')"
                    />
                </form-field>
                <form-field
                    class="column is-full-mobile is-half-desktop"
                    :error="error.localizations"
                    label="Brødtekst"
                >
                    <InputTextarea
                        v-model="description"
                        :error="error.localizations"
                        rows="3"
                        @input="localizationChanged('description')"
                    />
                </form-field>
                <form-field
                    class="column is-12"
                    :error="error.localizations"
                    label="Link"
                >
                    <input-text
                        v-model="link"
                        :error="error.localizations"
                        @input="localizationChanged('link')"
                    />
                </form-field>
            </div>
        </template>

        <template v-if="activeTab === 'subcategories' && isEdit">
            <ItemSubcategories
                :item-category="itemCategory"
                @updated="$emit('subcategories-updated')"
                @edit-subcategories="onEditSubcategories"
            />
        </template>

        <template
            v-if="activeTab === 'details'"
            v-slot:footer
        >
            <div class="footer-spacer">
                <div>
                    <button class="button is-danger is-flex-left" @click="reset">Nulstil</button>
                    <button class="button is-default" @click="findItems">Find varer</button>
                    <button class="button is-default" @click="$emit('closed')">Annuller</button>
                    <button class="button is-primary" @click="submit">{{ saveButtonText }}</button>
                </div>

                <button class="button is-danger" @click="remove" v-if="isEdit">Slet</button>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from '@elems/modal'
import {FormField, InputText, InputCheckbox, InputTextarea, InputDropzone} from '@elems/form'
import {mapGetters} from "vuex";
import ItemSubcategories from "./ItemSubcategories";

export default {
    components: {
        InputCheckbox,
        Modal,
        FormField,
        InputTextarea,
        InputText,
        InputDropzone,
        ItemSubcategories
    },
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        itemCategory: {
            type: Object,
            default: null,
        }
    },
    data() {
        return {
            activeTab: 'details',
            form: {
                uploadedFile: null,
                localizations: [],
            },
            image: null,
            error: {},
            locale_id: null,
            name: '',
            subtitle: '',
            description: '',
            link: '',
            tabs: [
                {
                    key: 'details',
                    title: 'Detaljer',
                    disabled: false,
                },
                {
                    key: 'subcategories',
                    title: 'Underkategorier',
                    disabled: true,
                },
            ],
        }
    },
    computed: {
        isEdit() {
            return this.itemCategory !== null
        },
        saveButtonText() {
            return this.isEdit ? 'Gem' : 'Opret'
        },
        ...mapGetters([
            'listLocales',
        ]),
        title() {
            if (this.isEdit) {
                if (this.activeTab === 'subcategories') {
                    return 'Rediger underkategori';
                }

                return 'Rediger kategori'
            }

            return 'Opret kategori';
        },
    },
    methods: {
        onEditSubcategories(row) {
            this.$emit('edit-subcategories', row);
        },
        findItems() {
            this.$router.push({
                name: 'item.index',
                query: {
                    'category': this.itemCategory.id,
                },
            });
        },
        setActiveTab(tab) {
            if (tab.disabled) {
                return;
            }

            this.activeTab = tab.key;
        },
        fileAdded(file, response) {
            this.form.uploadedFile = response;
            this.image = {
                'name': response.name,
                'url': response.url,
            }
        },
        removeImage() {
            this.form.uploadedFile = null;
            this.image = null;
            this.form.removeImage = this.itemCategory?.image ? true : false;
        },
        downloadImage() {
            fetch(this.image.url)
                .then(res => res.blob())
                .then(blob => {
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.style.display = 'none'
                    a.href = url
                    a.download = this.image.name
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                })
                .catch(() => alert('no file'))
        },
        submit() {
            return this.isEdit ? this.update() : this.create()
        },
        create() {
            window.axios.post('/api/item-categories', this.form)
                .then(({ data }) => {
                    this.$emit('created', data)
                    this.$store.dispatch('setSuccess', 'Kategori blev oprettet')
                    this.activeTab = 'subcategories';
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                    }
                })
        },
        update() {
            window.axios.put('/api/item-categories/' + this.itemCategory.id, this.form)
                .then(({ data }) => {
                    this.$emit('updated', data)
                    this.$store.dispatch('setSuccess', 'Kategori blev opdateret')
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                    }
                })
        },
        reset() {
            this.locale_id = this.listLocales[0].id;

            if (this.isEdit) {
                this.form = {
                    uploadedFile: null,
                    localizations: this.itemCategory.localizations.map((localization) => {
                        return {
                            locale_id: localization.locale_id,
                            key: localization.key,
                            value: localization.value,
                        }
                    }),
                }

                this.image = this.itemCategory.image ? {
                    'name': this.itemCategory.image_org_name,
                    'url': this.itemCategory.image,
                } : null

                this.updateLocalizations();
            } else {
                this.form = {
                    uploadedFile: null,
                    localizations: [],
                }

                this.image = null;

                this.name = '';
                this.subtitle = '';
                this.link = '';
                this.description = '';
            }
            this.error = {};
        },
        async remove() {
            await window.axios.get('/api/item-categories/' + this.itemCategory.id + '/in-use')
                .then(({data}) => {
                    if (data) {
                        this.error = { localizations: ['Kategorien er i brug og kan ikke slettes'] }
                    }
                })
                .catch(error => {
                    console.error(error);
                })

            if (Object.keys(this.error).length > 0) {
                return;
            }

            if (!confirm('Er du sikker på du vil slette ' + this.name + '?')) {
                return
            }

            window.axios.delete('/api/item-categories/' + this.itemCategory.id)
                .then(() => {
                    this.$emit('deleted', this.itemCategory)
                    this.$store.dispatch('setSuccess', 'Kategori blev slettet')
                })
                .catch(error => {
                    this.$store.dispatch('setError', 'Der skete en fejl')
                })
        },
        updateLocalizations() {
            this.updateLocalization('name');
            this.updateLocalization('description');
            this.updateLocalization('link');
            this.updateLocalization('subtitle');
            },
        updateLocalization(key) {
                const localization = this.form.localizations.find((localization) => localization.locale_id === parseInt(this.locale_id) && localization.key === key);
                this[key] = localization ? localization.value : '';
            },
        localizationChanged(key) {
            if (this[key].trim() === '' || this[key] === '<p><br></p>') {
                this.form.localizations = this.form.localizations.filter((localization) => !(localization.locale_id === parseInt(this.locale_id) && localization.key === key));
            } else {
                const localization = this.form.localizations.find((localization) => localization.locale_id === parseInt(this.locale_id) && localization.key === key);
                if (localization) {
                localization.value = this[key];
                } else {
                this.form.localizations.push({
                    locale_id: parseInt(this.locale_id),
                    key: key,
                    value: this[key],
                });
                }
            }

            // this.$emit('updated', this.form.localizations);
        },
        setTabDisabled(key, disabled) {
            this.tabs.find((tab) => tab.key === key).disabled = disabled;
        }
    },
    watch: {
        isEdit() {
            this.setTabDisabled('subcategories', !this.isEdit);
        },
        active() {
            if (this.active) {
                this.reset()
            }
        },
        locale_id() {
            this.updateLocalizations();
        },
    },
}
</script>

<style lang="scss" scoped>
.item-category-image {
    img {
        max-height: 200px;
        width: auto;
    }

    &--name {
        margin-top: 0.5rem;
        font-size: 12px;
        font-style: italic;
    }

    &--actions {
        display: flex;
        gap: 1rem;
        margin-top: 0.5rem;
        font-size: 12px;
        font-style: italic;

        &--remove {
            cursor: pointer;
            color: #ff3860;
        }

        &--download {
            cursor: pointer;
        }
    }
}
.footer-spacer {
    width: 100%;
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
}

.customer-details {
    h4 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #bbb;
        margin-bottom: 25px;
        cursor: pointer;

        i {
            transition: transform .3s ease-in-out;
        }
    }

    .column {
        display: none;
    }

    &.open {
        h4 i {
            transform: rotate(.5turn);
        }

        .column {
            display: block;
        }
    }


}

.column {
    padding: 0 0.75rem;
}
</style>
